/**
 * SAT Generator 3.0 — ResultsPage
 * Immediate post-test review with compact GGS diagrams (PR-D4-052).
 */

import { useLocation, useNavigate } from 'react-router-dom';
import type { Question, TestResult } from '../lib/types';
import { SessionReviewCard } from '../components/SessionReviewCard';
import './ReviewSessionPage.css';

interface ResultsState {
    questions: Question[];
    answers: [number, number][];
    results: TestResult[];
    score: number;
    total: number;
    timeSpent?: number;
    mode?: string;
    sessionId?: string | null;
}

export function ResultsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as ResultsState | null;

    if (!state) {
        return (
            <div className="review-session-page">
                <div className="review-session-page__hero-inner">
                    <h1 className="review-session-page__title">No results available</h1>
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const { questions, results, score, total } = state;
    const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
    const answersMap = new Map<number, number>(state.answers);

    const getGradeLabel = (pct: number) => {
        if (pct >= 80) return 'Excellent';
        if (pct >= 60) return 'Good Job';
        if (pct >= 40) return 'Keep Practicing';
        return 'More Practice Needed';
    };

    return (
        <div className="review-session-page">
            <header className="review-session-page__hero">
                <div className="review-session-page__hero-inner" style={{ textAlign: 'center' }}>
                    <p className="review-session-page__brand">SAT Generator</p>
                    <p className="review-session-page__subtitle" style={{ fontWeight: 700, color: '#0f172a' }}>
                        {getGradeLabel(percentage)}
                    </p>
                    <h1 className="review-session-page__title" style={{ fontSize: '2.5rem' }}>
                        {score}/{total}
                    </h1>
                    <p className="review-session-page__subtitle">{percentage}% correct</p>
                    <div className="mx-auto mt-6 h-3 max-w-xs overflow-hidden rounded-full bg-gray-200">
                        <div
                            className="h-full rounded-full bg-gray-400 transition-all duration-1000"
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </header>

            <div className="review-session-page__list">
                <h2 className="review-session-page__title review-session-page__list-heading" style={{ fontSize: '1.15rem' }}>
                    Question Review
                </h2>

                {questions.map((question, idx) => {
                    const result = results.find((r) => r.questionId === question.id);
                    const selectedOption =
                        answersMap.has(idx) ? (answersMap.get(idx) ?? null) : (result?.selectedOption ?? null);
                    const isCorrect =
                        result?.isCorrect ??
                        (selectedOption != null && selectedOption === question.correct_option_index);

                    return (
                        <SessionReviewCard
                            key={`${question.id}-${idx}`}
                            question={question}
                            questionNumber={idx + 1}
                            totalQuestions={total}
                            selectedOption={selectedOption}
                            isCorrect={Boolean(isCorrect)}
                            timeSpentSeconds={result?.timeSpentSeconds}
                        />
                    );
                })}

                <div className="flex justify-center pb-8">
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Practice Again
                    </button>
                </div>
            </div>
        </div>
    );
}
